// Czasochłonność (estimated_minutes): parsowanie wpisu i formatowanie etykiety.
// Spec: spec/lista-zadan.md → Inline editing → Czasochłonność. Czysty moduł bez DOM — tests/unit/duration_test.ts.

/** Dzień czasochłonności = 8 roboczogodzin (jak w narzędziach do planowania). */
export const HOURS_PER_DAY = 8;
const MIN_PER_HOUR = 60;
const MIN_PER_DAY = HOURS_PER_DAY * MIN_PER_HOUR;

const TOKEN = /(\d+(?:\.\d+)?)\s*(dni|dn|d|godziny|godzina|godzin|godz|g|h|minuty|minuta|minut|min|m)?(?=\s|\d|$)/y;

/**
 * Tekst → minuty. Akceptuje: same cyfry (minuty, jak dotąd), `h:mm`, oraz tokeny z jednostką
 * w dowolnej kolejności: `30m`, `30 min`, `1.5h` / `1,5h` / `1,5g`, `2h 30m`, `2h30`, `1d`, `1d 2h`.
 * Zwraca `null` dla pustego wpisu (wyczyść pole), `undefined` gdy nie da się sparsować.
 */
export function parseDuration(raw: string): number | null | undefined {
  const s = raw.trim().toLowerCase().replace(/,/g, '.');
  if (!s) return null;
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  const hm = s.match(/^(\d+):([0-5]\d)$/);
  if (hm) return parseInt(hm[1], 10) * MIN_PER_HOUR + parseInt(hm[2], 10);

  let i = 0;
  let total = 0;
  let any = false;
  while (i < s.length) {
    if (s[i] === ' ') { i++; continue; }
    TOKEN.lastIndex = i;
    const m = TOKEN.exec(s);
    if (!m) return undefined;
    const n = parseFloat(m[1]);
    const u = m[2] ?? '';
    const factor = u.startsWith('d') ? MIN_PER_DAY
      : (u === 'h' || u.startsWith('g')) ? MIN_PER_HOUR
      : 1;
    if (!u && TOKEN.lastIndex < s.length) return undefined;   // goła liczba tylko jako ostatni token ("2h30")
    total += n * factor;
    any = true;
    i = TOKEN.lastIndex;
  }
  return any ? Math.round(total) : undefined;
}

/** Minuty → `1d 2h 30min` (dzień = 8 h); 0 → `0min`. */
export function formatDuration(minutes: number): string {
  const d = Math.floor(minutes / MIN_PER_DAY);
  const h = Math.floor((minutes % MIN_PER_DAY) / MIN_PER_HOUR);
  const m = minutes % MIN_PER_HOUR;
  const parts: string[] = [];
  if (d) parts.push(d + 'd');
  if (h) parts.push(h + 'h');
  if (m || parts.length === 0) parts.push(m + 'min');
  return parts.join(' ');
}

/** Podpowiedź formatu do tooltipów i placeholderów. */
export const DURATION_HINT = 'np. 30m, 1.5h, 2h 30m, 1d (dzień = 8 h)';
